import { useState, useEffect } from "react";
import { getVerifiedUserId } from "../lib/userDataService";
import { deriveUserIntelligence } from "../lib/intelligenceService";

// ═══════════════════════════════════════════
// PHASE-BASED GUIDANCE (unchanged structure)
// ═══════════════════════════════════════════
const phaseGuidance = {
  menstrual: {
    name: "Menstrual Phase", icon: "period",
    description: "Your body needs rest and nourishment during this time",
    diet: {
      focus: "Iron-rich foods to replenish blood loss, anti-inflammatory foods",
      helpful: ["Leafy greens (spinach, kale)", "Lean red meat or lentils", "Dark chocolate", "Warm soups and stews", "Ginger tea"],
      avoid: ["Excess caffeine", "Processed foods", "Very cold foods", "High-sodium snacks", "Alcohol"]
    },
    exercise: {
      recommended: ["Gentle yoga", "Light walking", "Stretching", "Restorative poses", "Swimming (if comfortable)"],
      avoid: ["High-intensity workouts", "Heavy lifting", "Long runs"]
    },
    sleep: { duration: "8-9 hours recommended", tips: ["Go to bed earlier", "Use a heating pad for cramps", "Keep bedroom cool", "Avoid screens before bed"] },
    lifestyle: {
      daily: ["Practice self-compassion", "Take warm baths", "Journal your feelings"],
      stress: ["Meditation", "Deep breathing", "Light reading"],
      social: ["It's okay to cancel plans", "Ask for support if needed"]
    }
  },
  follicular: {
    name: "Follicular Phase", icon: "growth",
    description: "Energy is rising — great time to build habits and try new things",
    diet: {
      focus: "Light, fresh foods that support estrogen metabolism",
      helpful: ["Fresh vegetables", "Fermented foods", "Lean proteins", "Citrus fruits", "Flaxseeds"],
      avoid: ["Heavy fried foods", "Excess dairy", "Refined sugars"]
    },
    exercise: {
      recommended: ["Cardio workouts", "HIIT sessions", "Dance classes", "Strength training", "Outdoor activities"],
      avoid: ["Overtraining without rest", "Skipping warm-ups"]
    },
    sleep: { duration: "7-8 hours recommended", tips: ["Energy is higher, but don't skimp on sleep", "Maintain consistent wake time", "Morning sunlight exposure"] },
    lifestyle: {
      daily: ["Start new projects", "Schedule important meetings", "Social activities"],
      stress: ["Channel energy into productivity", "Creative pursuits"],
      social: ["Great time for networking", "Plan social events"]
    }
  },
  ovulation: {
    name: "Ovulation Phase", icon: "peak",
    description: "Peak energy and communication — your most social phase",
    diet: {
      focus: "Fiber-rich foods to support liver in processing estrogen surge",
      helpful: ["Raw vegetables", "Quinoa", "Berries", "Almonds", "Green smoothies"],
      avoid: ["Excess alcohol", "Processed foods", "High-sugar drinks"]
    },
    exercise: {
      recommended: ["High-intensity workouts", "Group fitness classes", "Competitive sports", "Peak performance training"],
      avoid: ["Ignoring hydration", "Overexertion without recovery"]
    },
    sleep: { duration: "7-8 hours recommended", tips: ["May feel less tired but still need rest", "Body temperature may rise", "Keep room cool"] },
    lifestyle: {
      daily: ["Schedule presentations", "Have important conversations", "Take on challenges"],
      stress: ["Your resilience is highest", "Good time for difficult tasks"],
      social: ["Peak social energy", "Great for dates or events", "Public speaking"]
    }
  },
  luteal: {
    name: "Luteal Phase", icon: "wind-down",
    description: "Time to slow down and prepare for the next cycle",
    diet: {
      focus: "Complex carbs and magnesium-rich foods for mood support",
      helpful: ["Sweet potatoes", "Brown rice", "Dark leafy greens", "Pumpkin seeds", "Banana"],
      avoid: ["Excess salt (causes bloating)", "Caffeine", "Sugar spikes", "Alcohol"]
    },
    exercise: {
      recommended: ["Pilates", "Yoga", "Light strength training", "Walking", "Swimming"],
      avoid: ["Extreme cardio", "High-stress workouts", "Pushing through fatigue"]
    },
    sleep: { duration: "8-9 hours recommended", tips: ["Progesterone makes you sleepy — honor it", "Naps are okay", "Magnesium before bed"] },
    lifestyle: {
      daily: ["Organize and plan", "Complete existing tasks", "Home-based activities"],
      stress: ["Practice extra self-care", "Limit commitments", "Journaling"],
      social: ["Smaller gatherings preferred", "Quality over quantity"]
    }
  },
  general: {
    name: "General PCOS Care", icon: "heart",
    description: "Daily guidance for managing PCOS symptoms",
    diet: {
      focus: "Anti-inflammatory, blood sugar-balancing foods",
      helpful: ["Leafy greens", "Lean proteins", "Healthy fats (avocado, olive oil)", "Berries", "Nuts and seeds"],
      avoid: ["Refined carbs", "Sugary drinks", "Processed foods", "Excess dairy", "Trans fats"]
    },
    exercise: {
      recommended: ["30 min daily movement", "Mix of cardio and strength", "Yoga for stress", "Walking after meals"],
      avoid: ["Sedentary lifestyle", "Over-exercising", "Skipping rest days"]
    },
    sleep: { duration: "7-9 hours recommended", tips: ["Consistent sleep schedule", "Dark, cool room", "Limit screens at night", "Manage stress before bed"] },
    lifestyle: {
      daily: ["Track your symptoms", "Stay hydrated", "Take prescribed supplements"],
      stress: ["Regular meditation", "Deep breathing exercises", "Time in nature"],
      social: ["Build support network", "Connect with PCOS community"]
    }
  }
};

// Doctor questions by condition
const doctorQuestions = {
  general: ["What tests should I take to check for PCOS?", "How is PCOS officially diagnosed?", "What are the first-line treatments for PCOS?", "Should I see an endocrinologist or gynecologist?", "Are there lifestyle changes that can help manage PCOS?"],
  irregularCycle: ["Is my cycle length considered irregular?", "Should I get hormone testing done?", "Could birth control help regulate my cycle?", "What could be causing my irregular periods?"],
  longCycle: ["Is a cycle longer than 35 days normal?", "Could I have oligomenorrhea?", "What tests can determine why my cycles are long?"],
  acne: ["Could my acne be caused by hormonal imbalance?", "Should I see a dermatologist for PCOS-related acne?", "Are there medications that target hormonal acne?"],
  hairLoss: ["Is my hair loss related to PCOS?", "What treatments are available for hormonal hair loss?", "Should I get my androgens tested?"],
  fatigue: ["Could my fatigue be related to insulin resistance?", "Should I get my thyroid checked?", "Is chronic fatigue common with PCOS?", "What supplements might help with PCOS fatigue?"],
  elevatedLHFSH: ["What does an elevated LH/FSH ratio mean?", "How does LH/FSH ratio affect ovulation?", "Should I get repeat hormone testing?"],
  insulinResistance: ["Should I be tested for insulin resistance?", "Would metformin be appropriate for me?", "How can I improve insulin sensitivity naturally?", "Am I at risk for type 2 diabetes?"],
  heavyBleeding: ["Is heavy bleeding during periods normal with PCOS?", "Should I get checked for anemia?"],
  painfulPeriods: ["Is severe menstrual pain normal with PCOS?", "Should I be checked for endometriosis?"],
};

// ═══════════════════════════════════════════
// ADAPTIVE CONTENT — rotates daily, personalized by user profile
// ═══════════════════════════════════════════
const adaptiveOverrides = {
  fatigue_heavy: {
    diet_extra: ["Magnesium-rich foods (spinach, almonds)", "B-vitamin foods (eggs, chickpeas)", "Iron-rich foods to combat tiredness"],
    exercise_swap: ["Restorative yoga", "Gentle walks in nature", "Light stretching sessions", "Tai chi"],
    sleep_extra: ["Consider magnesium supplement before bed", "Avoid caffeine after 2 PM", "Try a 20-minute power nap if needed"],
    lifestyle_extra: ["Break tasks into smaller chunks", "Prioritize energy-demanding work for mornings"],
  },
  acne_hormonal: {
    diet_extra: ["Anti-inflammatory turmeric/ginger", "Zinc-rich foods (pumpkin seeds)", "Probiotic-rich foods (yogurt, kimchi)"],
    diet_avoid_extra: ["Dairy products (may worsen acne)", "High-glycemic foods", "Whey protein supplements"],
    lifestyle_extra: ["Use gentle, non-comedogenic skincare", "Change pillowcase frequently"],
  },
  insulin_resistant: {
    diet_extra: ["Cinnamon (may improve insulin sensitivity)", "Apple cider vinegar before meals", "Chromium-rich foods (broccoli, grapes)"],
    diet_avoid_extra: ["White bread and pasta", "Fruit juices", "Sugary breakfast cereals"],
    exercise_swap: ["Walking after meals (helps blood sugar)", "Resistance training", "Swimming"],
    lifestyle_extra: ["Eat protein with every meal", "Avoid eating 3 hours before bed"],
  },
  stressed: {
    diet_extra: ["Adaptogenic herbs (ashwagandha tea)", "Omega-3 rich foods (walnuts, flax)", "Chamomile tea"],
    exercise_swap: ["Yoga", "Nature walks", "Breathing exercises", "Gentle swimming"],
    sleep_extra: ["Guided meditation before bed", "Lavender essential oil", "No screens 1 hour before bed"],
    lifestyle_extra: ["Practice 5-minute journaling", "Set boundaries with work"],
  },
  irregular_cycle: {
    diet_extra: ["Seed cycling (flax + pumpkin in follicular, sesame + sunflower in luteal)", "Vitex/chasteberry tea"],
    lifestyle_extra: ["Track basal body temperature", "Monitor cervical mucus changes"],
  },
  consistent_tracker: {
    lifestyle_extra: ["Your consistency is paying off — keep it up!", "Consider sharing insights with your doctor"],
  },
};

// Personalized note templates (rotated daily)
const personalizedNoteTemplates = {
  supportive: [
    "Your body is doing its best, and you're showing up for it. That matters.",
    "Healing isn't linear — some days are harder. That's okay.",
    "You don't have to do everything perfectly. Small steps count.",
  ],
  motivational: [
    "Every day you track is a day you understand yourself better.",
    "You're already ahead of most people in understanding your body.",
    "Consistency in care builds compound health benefits.",
  ],
  educational: [
    "PCOS affects each person differently — your care should reflect your unique pattern.",
    "Hormone fluctuations are normal in PCOS — tracking helps you anticipate them.",
    "Research shows lifestyle changes can significantly improve PCOS outcomes.",
  ],
  celebratory: [
    "You've been tracking consistently — that alone is an achievement!",
    "Your dedication to understanding your health is inspiring.",
    "Every check-in strengthens the accuracy of your personalized care.",
  ],
};

// ═══════════════════════════════════════════
// PERSONALIZATION ENGINE
// ═══════════════════════════════════════════
// buildUserProfile / phase-detection / streak-computation now live
// in `intelligenceService.deriveUserIntelligence` — the single
// source of truth. The rest of this file consumes that output.
// ═══════════════════════════════════════════

function getAdaptiveContent(profile, phase) {
  const base = phaseGuidance[phase] || phaseGuidance.general;
  const result = {
    diet: { ...base.diet, helpful: [...base.diet.helpful], avoid: [...base.diet.avoid] },
    exercise: { ...base.exercise, recommended: [...base.exercise.recommended] },
    sleep: { ...base.sleep, tips: [...base.sleep.tips] },
    lifestyle: { ...base.lifestyle, daily: [...base.lifestyle.daily], stress: [...base.lifestyle.stress] },
  };

  // Apply overrides based on dominant concerns
  for (const concern of profile.dominantConcerns) {
    const overrides = adaptiveOverrides[concern];
    if (!overrides) continue;

    if (overrides.diet_extra) result.diet.helpful = [...overrides.diet_extra, ...result.diet.helpful.slice(0, 3)];
    if (overrides.diet_avoid_extra) result.diet.avoid = [...overrides.diet_avoid_extra, ...result.diet.avoid.slice(0, 3)];
    if (overrides.exercise_swap) result.exercise.recommended = [...overrides.exercise_swap, ...result.exercise.recommended.slice(0, 2)];
    if (overrides.sleep_extra) result.sleep.tips = [...overrides.sleep_extra, ...result.sleep.tips.slice(0, 2)];
    if (overrides.lifestyle_extra) result.lifestyle.daily = [...overrides.lifestyle_extra, ...result.lifestyle.daily.slice(0, 2)];
  }

  return result;
}

function getPersonalizedNotes(profile, phase, data) {
  const notes = [];

  // Data-driven notes
  if (profile.tags.includes("elevated_lh_fsh")) notes.push("Elevated LH/FSH ratio detected — focus on hormone-balancing foods");
  if (profile.tags.includes("high_testosterone")) notes.push("Your testosterone levels suggest anti-androgen dietary focus may help");
  if (profile.tags.includes("insulin_resistant")) notes.push("Insulin markers suggest prioritizing blood sugar stability in meals");
  if (profile.tags.includes("fatigue_heavy")) notes.push("Your fatigue pattern suggests prioritizing recovery and iron-rich nutrition");
  if (profile.tags.includes("acne_active")) notes.push("Active acne suggests focusing on anti-inflammatory foods and gentle skincare");
  if (profile.tags.includes("stressed")) notes.push("Mood patterns suggest stress management should be a daily priority");
  if (profile.tags.includes("irregular_cycle")) notes.push("Your cycle variability may benefit from seed cycling and consistent routines");
  if (profile.tags.includes("overdue_period")) notes.push("Your period appears late — track symptoms closely and consider a checkup");
  if (profile.tags.includes("hair_loss_active")) notes.push("Hair concerns noted — anti-androgen nutrition may help");

  // Consistency-based
  if (data.streak >= 7) notes.push(`${data.streak}-day tracking streak! Your consistency makes your insights more accurate`);
  else if (data.streak >= 3) notes.push(`${data.streak} days tracking — patterns are starting to emerge`);

  // Cycle-specific
  if (phase === "menstrual" && data.cycle) notes.push("During your period, prioritize iron-rich foods and gentle movement");
  if (phase === "ovulation") notes.push("Peak energy phase — great time for challenging workouts and social events");

  // Daily rotation: add a tone-matched note
  const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
  const tonePool = personalizedNoteTemplates[profile.tone] || personalizedNoteTemplates.supportive;
  const rotatedNote = tonePool[dayOfYear % tonePool.length];
  if (notes.length < 4) notes.push(rotatedNote);

  return notes;
}

function getDoctorQuestionsForProfile(profile) {
  const questions = new Set();
  if (profile.tags.includes("irregular_cycle") || profile.tags.includes("overdue_period")) doctorQuestions.irregularCycle.forEach(q => questions.add(q));
  if (profile.tags.includes("elevated_lh_fsh")) doctorQuestions.elevatedLHFSH.forEach(q => questions.add(q));
  if (profile.tags.includes("insulin_resistant")) doctorQuestions.insulinResistance.forEach(q => questions.add(q));
  if (profile.tags.includes("acne_active") || profile.tags.includes("high_testosterone")) doctorQuestions.acne.forEach(q => questions.add(q));
  if (profile.tags.includes("hair_loss_active")) doctorQuestions.hairLoss.forEach(q => questions.add(q));
  if (profile.tags.includes("fatigue_heavy")) doctorQuestions.fatigue.forEach(q => questions.add(q));
  let list = Array.from(questions);
  if (list.length < 3) {
    const general = doctorQuestions.general.filter(q => !list.includes(q));
    list = [...list, ...general.slice(0, 5 - list.length)];
  }
  return list.slice(0, 5);
}

// Phase icons (SVG-based, no emojis)
const PhaseIcon = ({ phase }) => {
  const icons = {
    menstrual: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><circle cx="12" cy="12" r="4"/><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>,
    follicular: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M12 22c4-4 8-7.5 8-12a8 8 0 10-16 0c0 4.5 4 8 8 12z"/></svg>,
    ovulation: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
    luteal: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>,
    general: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>,
  };
  return icons[phase] || icons.general;
};

// ═══════════════════════════════════════════
// LOADING SKELETON
// ═══════════════════════════════════════════
const Skeleton = ({ width = "100%", height = 16, radius = 8 }) => (
  <div style={{
    width, height, borderRadius: radius, background: 'linear-gradient(90deg, #F3F4F6 25%, #E5E7EB 50%, #F3F4F6 75%)',
    backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite',
  }} />
);

const CardSkeleton = () => (
  <div style={{ background: '#fff', borderRadius: 20, padding: 20, marginBottom: 16, boxShadow: '0 4px 24px rgba(109,94,245,0.09)' }}>
    <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}><Skeleton width={40} height={40} radius={12} /><Skeleton width={120} height={20} /></div>
    <Skeleton width="80%" height={14} /><div style={{ height: 8 }} /><Skeleton width="60%" height={14} />
  </div>
);

// ═══════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════
export default function CareScreen({ userId }) {
  const [loading, setLoading] = useState(true);
  const [currentPhase, setCurrentPhase] = useState("general");
  const [cycleDay, setCycleDay] = useState(null);
  const [profile, setProfile] = useState({ tags: [], dominantConcerns: [], tone: "supportive" });
  const [adaptiveContent, setAdaptiveContent] = useState(null);
  const [personalizedNotes, setPersonalizedNotes] = useState([]);
  const [doctorQuestionsToShow, setDoctorQuestionsToShow] = useState([]);
  const [dataAvailable, setDataAvailable] = useState({ cycle: false, symptoms: false, hormones: false });

  useEffect(() => {
    const loadAllData = async () => {
      const uid = userId || (await getVerifiedUserId());
      if (!uid) { setLoading(false); return; }

      console.log('[CareScreen] Loading intelligence for user:', uid);

      // ── ONE call to the central intelligence layer ──
      const intel = await deriveUserIntelligence(uid, { withStreak: true });
      if (!intel) { setLoading(false); return; }

      // Map intelligence output to CareScreen's local state shape
      const phase = intel.carePhase || 'general';
      const day = intel.cycleDay;
      const userProfile = intel.profile;
      const userData = {
        cycle: intel.cycle,
        hormones: intel.hormones,
        symptoms: intel.weekSymptoms,
        checkins: intel.todayCheckin,
        onboarding: intel.onboarding,
        streak: intel.streak,
      };

      const content = getAdaptiveContent(userProfile, phase);
      const notes = getPersonalizedNotes(userProfile, phase, userData);
      const questions = getDoctorQuestionsForProfile(userProfile);

      console.log('[CareScreen] Profile tags:', userProfile.tags);
      console.log('[CareScreen] Dominant concerns:', userProfile.dominantConcerns);
      console.log('[CareScreen] Tone:', userProfile.tone);

      setCycleDay(day);
      setCurrentPhase(phase);
      setProfile(userProfile);
      setAdaptiveContent(content);
      setPersonalizedNotes(notes);
      setDoctorQuestionsToShow(questions);
      setDataAvailable({
        cycle:    intel.dataAvailable.cycle,
        symptoms: intel.dataAvailable.symptoms,
        hormones: intel.dataAvailable.hormones,
      });
      setLoading(false);
    };

    loadAllData();
  }, [userId]);

  // ── Loading state ──
  if (loading) {
    return (
      <div className="care-screen" data-testid="care-screen-loading" style={{ paddingBottom: 100 }}>
        <div style={{ background: 'linear-gradient(135deg, #C4B5FD, #A78BFA)', borderRadius: 20, padding: 24, marginBottom: 20 }}>
          <Skeleton width={120} height={12} /><div style={{ height: 12 }} />
          <Skeleton width="70%" height={22} /><div style={{ height: 8 }} />
          <Skeleton width="90%" height={14} />
        </div>
        <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
        <CardSkeleton /><CardSkeleton /><CardSkeleton />
      </div>
    );
  }

  const guidance = adaptiveContent || phaseGuidance[currentPhase] || phaseGuidance.general;
  const baseGuidance = phaseGuidance[currentPhase] || phaseGuidance.general;
  const hasAnyData = dataAvailable.cycle || dataAvailable.symptoms || dataAvailable.hormones;

  return (
    <div className="care-screen" data-testid="care-screen" style={{ paddingBottom: 100 }}>
      <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>

      {/* Header Card */}
      <div style={{
        background: 'linear-gradient(135deg, #A78BFA, #6D5EF5)', borderRadius: 20,
        padding: 20, marginBottom: 20, color: '#fff'
      }} data-testid="care-header-card">
        <div style={{ fontSize: 11, textTransform: 'uppercase', opacity: 0.85, letterSpacing: '0.1em', marginBottom: 8 }}>
          {hasAnyData ? 'YOUR PERSONALIZED CARE' : "TODAY'S PCOS CARE"}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'rgba(255,255,255,0.2)', display: 'flex',
            alignItems: 'center', justifyContent: 'center'
          }}>
            <PhaseIcon phase={currentPhase} />
          </div>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 2 }}>{baseGuidance.name}</div>
            <div style={{ fontSize: 14, opacity: 0.9 }}>{baseGuidance.description}</div>
            {cycleDay && (
              <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>Cycle Day {cycleDay}</div>
            )}
          </div>
        </div>
        {profile.tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 12 }}>
            {profile.dominantConcerns.slice(0, 3).map((c, i) => (
              <span key={i} style={{
                background: 'rgba(255,255,255,0.2)', borderRadius: 12,
                padding: '3px 10px', fontSize: 11, color: '#fff'
              }}>
                {c.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Missing data banner — only when truly no data */}
      {!hasAnyData && (
        <div style={{
          background: '#FEF3C7', borderRadius: 12, padding: '12px 16px',
          marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#92400E" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#92400E' }}>Add health data for personalized guidance</div>
            <div style={{ fontSize: 12, color: '#B45309' }}>Track your cycle, symptoms, and hormones to unlock adaptive recommendations</div>
          </div>
        </div>
      )}

      {/* Diet Section */}
      <div style={{ background: '#fff', borderRadius: 20, padding: 20, marginBottom: 16, boxShadow: '0 4px 24px rgba(109,94,245,0.09)' }} data-testid="diet-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg, #D1FAE5, #A7F3D0)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#065F46" strokeWidth="2"><path d="M12 22c4.97 0 9-2.24 9-5v-1c0-2.76-4.03-5-9-5s-9 2.24-9 5v1c0 2.76 4.03 5 9 5z"/><path d="M12 6c2.76 0 5-1.12 5-2.5S14.76 1 12 1 7 2.12 7 3.5 9.24 6 12 6z"/></svg>
          </div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#2F2F35', margin: 0 }}>Diet</h3>
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>Focus on:</div>
          <p style={{ fontSize: 14, color: '#374151', margin: 0, lineHeight: 1.5 }}>{guidance.diet.focus}</p>
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#059669', marginBottom: 6 }}>Helpful foods:</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {guidance.diet.helpful.map((food, idx) => (
              <span key={idx} style={{ background: '#D1FAE5', color: '#065F46', padding: '4px 10px', borderRadius: 10, fontSize: 12 }}>{food}</span>
            ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#DC2626', marginBottom: 6 }}>Avoid:</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {guidance.diet.avoid.map((food, idx) => (
              <span key={idx} style={{ background: '#FEE2E2', color: '#991B1B', padding: '4px 10px', borderRadius: 10, fontSize: 12 }}>{food}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Exercise Section */}
      <div style={{ background: '#fff', borderRadius: 20, padding: 20, marginBottom: 16, boxShadow: '0 4px 24px rgba(109,94,245,0.09)' }} data-testid="exercise-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg, #DBEAFE, #BFDBFE)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D4ED8" strokeWidth="2"><path d="M18 8h1a4 4 0 010 8h-1M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>
          </div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#2F2F35', margin: 0 }}>Exercise</h3>
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#1D4ED8', marginBottom: 6 }}>Recommended:</div>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {guidance.exercise.recommended.map((ex, idx) => (
              <li key={idx} style={{ fontSize: 13, color: '#374151', marginBottom: 4 }}>{ex}</li>
            ))}
          </ul>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#DC2626', marginBottom: 6 }}>Avoid:</div>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {guidance.exercise.avoid.map((ex, idx) => (
              <li key={idx} style={{ fontSize: 13, color: '#6B7280', marginBottom: 4 }}>{ex}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Sleep & Recovery */}
      <div style={{ background: '#fff', borderRadius: 20, padding: 20, marginBottom: 16, boxShadow: '0 4px 24px rgba(109,94,245,0.09)' }} data-testid="sleep-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg, #EDE9FE, #DDD6FE)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6D28D9" strokeWidth="2"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
          </div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#2F2F35', margin: 0 }}>Sleep & Recovery</h3>
        </div>
        <div style={{ background: '#F3F4F6', borderRadius: 12, padding: '12px 14px', marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>{guidance.sleep.duration}</div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>Tips:</div>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {guidance.sleep.tips.map((tip, idx) => (
            <li key={idx} style={{ fontSize: 13, color: '#374151', marginBottom: 4 }}>{tip}</li>
          ))}
        </ul>
      </div>

      {/* Lifestyle */}
      <div style={{ background: '#fff', borderRadius: 20, padding: 20, marginBottom: 16, boxShadow: '0 4px 24px rgba(109,94,245,0.09)' }} data-testid="lifestyle-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg, #FEF3C7, #FDE68A)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#92400E" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
          </div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#2F2F35', margin: 0 }}>Lifestyle</h3>
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#92400E', marginBottom: 6 }}>Daily suggestions:</div>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {guidance.lifestyle.daily.map((item, idx) => (
              <li key={idx} style={{ fontSize: 13, color: '#374151', marginBottom: 4 }}>{item}</li>
            ))}
          </ul>
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 6 }}>Stress management:</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {guidance.lifestyle.stress.map((item, idx) => (
              <span key={idx} style={{ background: '#F3F4F6', color: '#374151', padding: '4px 10px', borderRadius: 10, fontSize: 12 }}>{item}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Personalized Note */}
      <div style={{ background: 'linear-gradient(135deg, #EDE9FE, #DDD6FE)', borderRadius: 20, padding: 20, marginBottom: 16, border: '2px solid #A78BFA' }} data-testid="personalized-note-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg, #A78BFA, #6D5EF5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/></svg>
          </div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#5B21B6', margin: 0 }}>Personalized Note</h3>
        </div>
        {personalizedNotes.length > 0 ? (
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {personalizedNotes.map((note, idx) => (
              <li key={idx} style={{ fontSize: 14, color: '#4C1D95', marginBottom: 8, lineHeight: 1.5 }}>{note}</li>
            ))}
          </ul>
        ) : (
          <p style={{ fontSize: 14, color: '#6D28D9', margin: 0, lineHeight: 1.5 }}>
            {hasAnyData
              ? `You're in your ${baseGuidance.name.toLowerCase()}. Focus on ${currentPhase === 'menstrual' ? 'rest and nourishment' : currentPhase === 'follicular' ? 'building new habits' : currentPhase === 'ovulation' ? 'peak performance' : 'self-care and preparation'}.`
              : "Start tracking your symptoms, cycle, and hormones to unlock care tailored to your unique PCOS pattern."}
          </p>
        )}
      </div>

      {/* Doctor Questions */}
      <div style={{ background: '#fff', borderRadius: 20, padding: 20, marginBottom: 16, boxShadow: '0 4px 24px rgba(109,94,245,0.09)', border: '1px solid #E5E7EB' }} data-testid="doctor-questions-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg, #DBEAFE, #BFDBFE)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D4ED8" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
          </div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#2F2F35', margin: 0 }}>Questions for Your Doctor</h3>
        </div>
        <p style={{ fontSize: 12, color: '#6B7280', marginBottom: 14, marginTop: 0 }}>
          {hasAnyData ? 'Based on your health data, consider discussing:' : 'Common questions to discuss:'}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {doctorQuestionsToShow.map((question, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', background: '#F9FAFB', borderRadius: 12, borderLeft: '3px solid #6D5EF5' }}>
              <span style={{ color: '#6D5EF5', fontWeight: 700, fontSize: 14, minWidth: 20 }}>{idx + 1}.</span>
              <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{question}</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 16, padding: '10px 14px', background: '#FEF3C7', borderRadius: 10 }}>
          <p style={{ fontSize: 11, color: '#92400E', margin: 0, lineHeight: 1.5 }}>
            Note: These are suggestions to guide your conversation. Always share your complete health history with your healthcare provider.
          </p>
        </div>
      </div>

      {/* Disclaimer */}
      <div style={{ textAlign: 'center', padding: '12px 20px', marginBottom: 20 }}>
        <p style={{ fontSize: 11, color: '#9CA3AF', margin: 0, fontStyle: 'italic' }}>
          This guidance is for educational purposes only and does not replace professional medical advice.
        </p>
      </div>
    </div>
  );
}
